import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';
import { identify, resetIdentity, track } from '../utils/analytics';
import { setSentryUser } from '../utils/sentry';
import { callEdgeFunction } from '../utils/supabaseFunctions';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  // S26 (2026-06-04) — admin role surfaced through context so the
  // Header (Dashboard button), AdminPage (access gate), and any
  // future admin-only UI all read from one source instead of
  // racing separate is_admin lookups. `null` while still checking
  // (or when no user is signed in); `true`/`false` once resolved.
  // Re-checked on auth state changes.
  isAdmin: boolean | null;
  signIn: (email: string, password: string) => Promise<void>;
  // M-H — closed-beta gate: signup goes through the signup-with-invite
  // edge function which validates BETA_INVITE_CODE before admin-creating
  // the user. Pass an empty string for the invite code when the gate
  // isn't active (the edge fn falls through with no code required when
  // BETA_INVITE_CODE is unset on the server).
  signUp: (email: string, password: string, inviteCode?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  updatePassword: (newPassword: string) => Promise<boolean>;
  // True between the user clicking a password-recovery email link and them
  // completing the "Set New Password" flow. The app uses this to open the
  // AuthModal directly into updatePassword mode on landing.
  inPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inPasswordRecovery, setInPasswordRecovery] = useState(false);
  // S26 (2026-06-04) — admin-role state. Null = checking / no user;
  // resolved to true/false once we've read public.users.is_admin.
  // The query uses the user's own RLS (a user can SELECT their own
  // row), so no privileged path is needed.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  // Resolve is_admin from public.users for the currently signed-in
  // user. Idempotent: safe to call multiple times. Returns the
  // resolved value AND updates state, so callers don't need to
  // double-await.
  const refreshAdmin = async (u: User | null) => {
    if (!u) { setIsAdmin(null); return; }
    try {
      const { data, error } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', u.id)
        .maybeSingle();
      setIsAdmin(!error && data?.is_admin === true);
    } catch {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoading(false);
      // S26 — resolve admin role for the restored session.
      refreshAdmin(u);
    });

    // Listen for changes on auth state. PASSWORD_RECOVERY fires when the user
    // arrives via a password-reset email link (supabase-js parses the hash
    // fragment and dispatches automatically). We flip a flag the AuthModal
    // watches so it opens directly into "Set New Password" mode.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoading(false);
      if (event === 'PASSWORD_RECOVERY') setInPasswordRecovery(true);

      // M-C — keep Sentry's user scope + PostHog identity in sync with
      // the auth state. SIGNED_OUT clears both; SIGNED_IN identifies once
      // (PostHog dedups by distinct id) so refreshes don't recapture.
      if (event === 'SIGNED_IN' && u) {
        identify(u.id, { email_domain: u.email?.split('@')[1] });
        setSentryUser({ id: u.id, email: u.email });
        refreshAdmin(u);  // S26
      } else if (event === 'SIGNED_OUT') {
        resetIdentity();
        setSentryUser(null);
        setIsAdmin(null);  // S26
      } else if (event === 'TOKEN_REFRESHED' && u) {
        // S26 — Don't refetch on every token refresh (cheap to be lazy
        // here; the role rarely changes mid-session). If we ever surface
        // a "promoted to admin" flow inside the running app, call
        // refreshAdmin() from there instead.
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, inviteCode?: string) => {
    try {
      setError(null);
      // S52 (2026-06-06) — Read the `?invite=...` URL parameter, which
      // is the email_invitations token an admin sent via Resend. If
      // present, we pass it as `email_invitation_token` alongside the
      // typed code. signup-with-invite accepts either gate; the token
      // wins when both are set (it's recipient-bound — more specific).
      // Without this, recipients of admin email invites had to manually
      // hunt for a 5-segment invitation_code from somewhere because the
      // URL parameter was ignored client-side.
      let emailInvToken = '';
      try {
        const params = new URLSearchParams(window.location.search);
        emailInvToken = (params.get('invite') ?? '').trim();
      } catch {
        // window.location absent (SSR / non-browser env) — fall through.
      }
      // M-H — route signups through the edge function so the closed-beta
      // gate is enforced server-side. The function admin-creates the
      // user (email confirmation skipped — invite IS the gate), so we
      // immediately sign in after creation.
      //
      // S53 (2026-06-06) — 20s client timeout. Pre-S53 a slow / hung
      // edge function (Supabase rate-limit eating the request, cold
      // start, etc.) made the form spin indefinitely with no error
      // message. Real user case: invitee saw "spinning forever" after
      // clicking Create Account; we couldn't tell whether the request
      // never went out, never returned, or returned an error the UI
      // failed to render. Now: AbortController fires at 20s, the await
      // rejects with a clear timeout message, modal renders it.
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 20_000);
      let response: Response;
      try {
        response = await callEdgeFunction('signup-with-invite', {
          email,
          password,
          code: inviteCode ?? '',
          email_invitation_token: emailInvToken,
        }, controller.signal);
      } catch (fetchErr) {
        // Network-layer / abort failures land here. Re-throw with a
        // friendlier message so the modal can show it.
        if (fetchErr instanceof Error && fetchErr.name === 'AbortError') {
          throw new Error(
            'The signup request took longer than 20 seconds — either Supabase is rate-limiting your IP, or the network is slow. ' +
            'Try again in a minute, or use a different network (mobile data instead of Wi-Fi works).',
          );
        }
        throw fetchErr;
      } finally {
        window.clearTimeout(timeoutId);
      }
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { message?: string } | null;
        throw new Error(body?.message || `Sign-up failed (${response.status})`);
      }
      // Edge fn returns { ok: true } and the user exists — sign them in
      // with the password they just chose.
      // S53 — wrap signInWithPassword with a 15s timeout too. If the
      // server-side user creation succeeded but signin hangs on
      // Supabase auth rate-limiting, the user STILL has an account —
      // they just can't auto-sign-in. Surface a clear message
      // pointing them to the "Sign in" tab so they can retry without
      // recreating the account.
      const signInTimeout = new Promise<never>((_, reject) =>
        window.setTimeout(
          () => reject(new Error(
            'Your account was created, but auto sign-in took too long. ' +
            'Close this dialog and click "Sign in" — you can log in with the email + password you just chose.',
          )),
          15_000,
        ),
      );
      const signInResult = await Promise.race([
        supabase.auth.signInWithPassword({ email, password }),
        signInTimeout,
      ]);
      const signInErr = (signInResult as { error?: { message?: string } } | undefined)?.error;
      if (signInErr) throw signInErr;
      // M-C — top of the funnel. PostHog dedups by distinct id so multiple
      // signups on the same id won't be double-counted.
      track('signup_completed', { email_domain: email.split('@')[1] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign up');
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign in');
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sign out');
    }
  };

  // Send a password-recovery email. Returns true on success so the UI can
  // flip to a "check your inbox" state without needing to inspect `error`.
  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/?password_recovery=1`,
      });
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred sending the reset email');
      return false;
    }
  };

  // Called from the "Set New Password" form once the user has clicked through
  // a recovery email link. Supabase has already promoted the recovery token to
  // a real session, so updateUser() does the actual password write.
  const updatePassword = async (newPassword: string): Promise<boolean> => {
    try {
      setError(null);
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setInPasswordRecovery(false);
      // Strip the recovery params from the URL so a refresh doesn't re-open the flow.
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred updating your password');
      return false;
    }
  };

  const clearPasswordRecovery = () => setInPasswordRecovery(false);
  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user, loading, isAdmin,
        signIn, signUp, signOut, resetPassword, updatePassword,
        inPasswordRecovery, clearPasswordRecovery,
        error, clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};